import React, { useState } from 'react';
import { PackageIcon, TruckIcon, CheckCircleIcon, ClockIcon, SearchIcon, CalendarIcon, FilterIcon, ArrowUpDownIcon, EyeIcon, RefreshCwIcon, MessageSquareIcon, ChevronLeftIcon, ChevronRightIcon, StarIcon } from 'lucide-react';
import { useDarkMode } from '../context/DarkModeContext';
import { useDarkClasses } from '../hooks/useDarkClasses';
import { useRoleTheme } from '../hooks/useRoleTheme';
import { WriteReviewModal, ReviewDraft, ReviewSubject, ReviewSubjectType } from '../components/Reviews/WriteReviewModal';
const MyOrders = () => {
  const {
    darkMode
  } = useDarkMode();
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  // Mock data - expanded with more orders and statuses
  const orders = [{
    id: 'ORD-001',
    date: 'Oct 1, 2023',
    items: [{
      id: 'item-1',
      title: 'The Silent Echo',
      format: 'Paperback',
      price: 14.99,
      image: 'https://images.unsplash.com/photo-1544947950-fa07a98d237f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
    }],
    status: 'Delivered',
    total: 14.99
  }, {
    id: 'ORD-002',
    date: 'Oct 5, 2023',
    items: [{
      id: 'item-2',
      title: 'Beyond the Veil',
      format: 'eBook',
      price: 9.99,
      image: 'https://images.unsplash.com/photo-1543002588-bfa74002ed7e?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
    }, {
      id: 'item-3',
      title: 'Whispers in the Dark',
      format: 'Audiobook',
      price: 19.99,
      image: 'https://images.unsplash.com/photo-1621351183012-e2f9972dd9bf?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
    }],
    status: 'Processing',
    total: 29.98
  }, {
    id: 'ORD-003',
    date: 'Sep 22, 2023',
    items: [{
      id: 'item-4',
      title: 'Midnight Chronicles',
      format: 'Hardcover',
      price: 24.99,
      image: 'https://images.unsplash.com/photo-1633477189729-9290b3261d0a?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
    }],
    status: 'Delivered',
    total: 24.99
  }, {
    id: 'ORD-004',
    date: 'Aug 15, 2023',
    items: [{
      id: 'item-5',
      title: 'The Lost Kingdom',
      format: 'Paperback',
      price: 12.99,
      image: 'https://images.unsplash.com/photo-1589998059171-988d887df646?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
    }, {
      id: 'item-6',
      title: 'Fantasy Realms Bookmark Set',
      format: 'Merchandise',
      price: 8.99,
      image: 'https://images.unsplash.com/photo-1544947987-f6c4fa25fa4a?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
    }],
    status: 'Delivered',
    total: 21.98
  }];
  // State for filters and search
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  // Review Modal State
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [reviewSubject, setReviewSubject] = useState<ReviewSubject | undefined>(undefined);
  const [reviewedItems, setReviewedItems] = useState<string[]>(['item-1']); // Mock one already reviewed
  // Calculate order statistics
  const totalOrders = orders.length;
  const totalSpent = orders.reduce((sum, order) => sum + order.total, 0);
  const deliveredOrders = orders.filter(order => order.status === 'Delivered').length;
  // Filter and sort orders
  const filteredOrders = orders.filter(order => {
    // Status filter
    if (statusFilter !== 'All' && order.status !== statusFilter) return false;
    // Search query filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      if (order.id.toLowerCase().includes(query)) return true;
      if (order.items.some(item => item.title.toLowerCase().includes(query))) return true;
      return false;
    }
    return true;
  }).sort((a, b) => {
    // Sort by date or total
    if (sortBy === 'date') {
      const dateA = new Date(a.date).getTime();
      const dateB = new Date(b.date).getTime();
      return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
    } else {
      return sortOrder === 'asc' ? a.total - b.total : b.total - a.total;
    }
  });
  // Toggle sort order
  const toggleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };
  // Get status icon based on status
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'Delivered':
        return <CheckCircleIcon className="h-4 w-4 text-green-600 mr-1" data-id="element-11301" data-mp="3rwvlwvlxeorg" />;
      case 'Processing':
        return <ClockIcon className="h-4 w-4 text-yellow-600 mr-1" data-id="element-11302" data-mp="3nvs6ga7siuqr" />;
      case 'Shipped':
        return <TruckIcon className="h-4 w-4 text-blue-600 mr-1" data-id="element-11303" data-mp="3j4jzrlxuhnha" />;
      default:
        return <PackageIcon className="h-4 w-4 text-gray-600 mr-1" data-id="element-11304" data-mp="3j2gz87z2gbyb" />;
    }
  };
  // Get status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Delivered':
        return darkMode ? 'bg-green-900/30 text-green-300' : 'bg-green-100 text-green-800';
      case 'Processing':
        return darkMode ? 'bg-yellow-900/30 text-yellow-300' : 'bg-yellow-100 text-yellow-800';
      case 'Shipped':
        return darkMode ? 'bg-blue-900/30 text-blue-300' : 'bg-blue-100 text-blue-800';
      default:
        return darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-800';
    }
  };
  const getFormatType = (format: string): ReviewSubjectType => {
    const f = format.toLowerCase();
    if (f.includes('merch') || f.includes('bookmark') || f.includes('apparel')) return 'merch';
    if (f.includes('audio')) return 'audiobook';
    if (f.includes('box')) return 'box';
    return 'book';
  };
  const openReviewModal = (item: any) => {
    setReviewSubject({
      id: item.id,
      type: getFormatType(item.format),
      title: item.title,
      image: item.image
    });
    setIsReviewModalOpen(true);
  };
  const handleSaveReview = (draft: ReviewDraft) => {
    if (draft.subject.id) {
      setReviewedItems(prev => [...prev, draft.subject.id.toString()]);
    }
    setIsReviewModalOpen(false);
  };
  return <div className={`min-h-screen py-8 ${darkMode ? 'bg-gray-950' : 'bg-gray-50'}`} data-id="element-11305" data-mp="1r8hmzzamisiy">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-11306" data-mp="av0zjpinsz2c">
        <div className="md:flex md:items-center md:justify-between mb-6" data-id="element-11307" data-mp="awu6z713jnbe">
          <div data-id="element-11308" data-mp="fvf6t5cfs4zb">
            <h1 className={`text-3xl font-bold ${dk.textHeading}`} data-id="element-11309" data-mp="fv5c54gg59na">
              My Orders
            </h1>
            <p className={`mt-1 text-lg ${dk.textSecondary}`} data-id="element-11310" data-mp="8cflgu5f03db">
              Track and manage your purchases
            </p>
          </div>
        </div>

        {/* Order Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6" data-id="element-11311" data-mp="ha82q3up5l3c">
          <div className={`rounded-lg shadow-md p-4 border ${dk.card}`} data-id="element-11312" data-mp="cczm47p75p47">
            <p className={`text-sm font-medium ${dk.textMuted}`} data-id="element-11313" data-mp="3kr864rdnl7kr">
              Total Orders
            </p>
            <p className={`text-2xl font-bold ${dk.textHeading}`} data-id="element-11314" data-mp="3gqxqen0272sp">
              {totalOrders}
            </p>
          </div>
          <div className={`rounded-lg shadow-md p-4 border ${dk.card}`} data-id="element-11315" data-mp="3uib4vmj41agv">
            <p className={`text-sm font-medium ${dk.textMuted}`} data-id="element-11316" data-mp="3pqkr16kys4kp">Total Spent</p>
            <p className={`text-2xl font-bold ${dk.textHeading}`} data-id="element-11317" data-mp="320ywiwfjedj1">
              ${totalSpent.toFixed(2)}
            </p>
          </div>
          <div className={`rounded-lg shadow-md p-4 border ${dk.card}`} data-id="element-11318" data-mp="2x1du2c15rn1k">
            <p className={`text-sm font-medium ${dk.textMuted}`} data-id="element-11319" data-mp="2cgu0mtll9nbe">
              Delivered Orders
            </p>
            <p className={`text-2xl font-bold ${dk.textHeading}`} data-id="element-11320" data-mp="2giyplf13dvrp">
              {deliveredOrders}
            </p>
          </div>
        </div>

        {/* Search and Filters */}
        <div className={`rounded-lg shadow-md p-4 mb-6 border ${dk.card}`} data-id="element-11321" data-mp="2qgml476iv7ax">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4" data-id="element-11322" data-mp="2vf67u9sxwn4c">
            <div className="relative flex-grow max-w-md" data-id="element-11323" data-mp="2vdd8zgokj76w">
              <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" data-id="element-11324" data-mp="2zfgn6d2mgpvv" />
              <input type="text" placeholder="Search orders by ID or product..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className={`pl-10 pr-4 py-2 w-full border rounded-lg focus:outline-none focus:ring-2 ${rt.inputFocus} ${dk.input}`} data-id="element-11325" data-mp="3489th5dqo6a2" />
            </div>
            <div className="flex flex-wrap gap-2" data-id="element-11326" data-mp="17i6st5do1s3j">
              <button onClick={() => setStatusFilter('All')} className={`px-3 py-2 rounded-md text-sm font-medium ${statusFilter === 'All' ? `${rt.bgMedium} ${rt.textPrimary}` : darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-11327" data-mp="12ic3355mn8k2">
                All
              </button>
              <button onClick={() => setStatusFilter('Processing')} className={`px-3 py-2 rounded-md text-sm font-medium flex items-center ${statusFilter === 'Processing' ? darkMode ? 'bg-yellow-900/30 text-yellow-300' : 'bg-yellow-100 text-yellow-700' : darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-11328" data-mp="1q9zl0xkwv9a7">
                <ClockIcon className="h-4 w-4 mr-1" data-id="element-11329" data-mp="29090va9mezkd" />
                Processing
              </button>
              <button onClick={() => setStatusFilter('Shipped')} className={`px-3 py-2 rounded-md text-sm font-medium flex items-center ${statusFilter === 'Shipped' ? darkMode ? 'bg-blue-900/30 text-blue-300' : 'bg-blue-100 text-blue-700' : darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-11330" data-mp="b1ox0kbq90xk">
                <TruckIcon className="h-4 w-4 mr-1" data-id="element-11331" data-mp="u0jdpyur4bgd" />
                Shipped
              </button>
              <button onClick={() => setStatusFilter('Delivered')} className={`px-3 py-2 rounded-md text-sm font-medium flex items-center ${statusFilter === 'Delivered' ? darkMode ? 'bg-green-900/30 text-green-300' : 'bg-green-100 text-green-700' : darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-11332" data-mp="pyxtfsfw0uw5">
                <CheckCircleIcon className="h-4 w-4 mr-1" data-id="element-11333" data-mp="18pp91bqgxhiz" />
                Delivered
              </button>
            </div>
          </div>
        </div>

        {/* Sort Controls */}
        <div className="flex justify-between items-center mb-4" data-id="element-11334" data-mp="1s9efrrtb33i2">
          <div className={`text-sm ${dk.textMuted}`} data-id="element-11335" data-mp="1wcaqyxginxbz">
            Showing {filteredOrders.length} of {orders.length} orders
          </div>
          <div className="flex items-center space-x-2" data-id="element-11336" data-mp="1nc5rralh858w">
            <span className={`text-sm ${dk.textMuted}`} data-id="element-11337" data-mp="1nakxlm32qobk">Sort by:</span>
            <button onClick={() => toggleSort('date')} className={`px-3 py-1 text-sm rounded flex items-center ${sortBy === 'date' ? `${rt.bgMedium} ${rt.textPrimary}` : darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700'}`} data-id="element-11338" data-mp="18mzuvta42r46">
              Date{' '}
              {sortBy === 'date' && <ArrowUpDownIcon className="h-3 w-3 ml-1" data-id="element-11339" data-mp="14jk3bbcwfgyc" />}
            </button>
            <button onClick={() => toggleSort('total')} className={`px-3 py-1 text-sm rounded flex items-center ${sortBy === 'total' ? `${rt.bgMedium} ${rt.textPrimary}` : darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700'}`} data-id="element-11340" data-mp="pqgvhvp40hkt">
              Amount{' '}
              {sortBy === 'total' && <ArrowUpDownIcon className="h-3 w-3 ml-1" data-id="element-11341" data-mp="1s97nydjrone" />}
            </button>
          </div>
        </div>

        {/* Orders List */}
        <div className="space-y-4" data-id="element-11342" data-mp="3sx8zvga98rgr">
          {filteredOrders.length > 0 ? filteredOrders.map(order => <div key={order.id} className={`rounded-lg shadow-md overflow-hidden border ${dk.card}`} data-id="element-11343" data-mp="piwhip70wocl">
                {/* Order Header */}
                <div className={`px-6 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between border-b ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'}`} data-id="element-11344" data-mp="fkgyl9i5gle6">
                  <div className="flex items-center mb-2 sm:mb-0" data-id="element-11345" data-mp="30aep5b38p6gh">
                    <PackageIcon className="h-5 w-5 text-indigo-600 mr-2" data-id="element-11346" data-mp="1fsng4d15l0i9" />
                    <div data-id="element-11347" data-mp="1fqkeswujvky8">
                      <h2 className={`text-lg font-semibold ${dk.textHeading}`} data-id="element-11348" data-mp="1krqc77z5nwe3">
                        Order {order.id}
                      </h2>
                      <div className={`flex items-center text-sm ${dk.textMuted}`} data-id="element-11349" data-mp="1pq1m1ipga6wb">
                        <CalendarIcon className="h-4 w-4 mr-1" data-id="element-11350" data-mp="1ujct9ky6cpor" />
                        <span data-id="element-11351" data-mp="1zfuuiauaq5wc">Placed on {order.date}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center" data-id="element-11352" data-mp="23k2kscgejtwo">
                    <span className={`flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(order.status)}`} data-id="element-11353" data-mp="28iceeimhamg6">
                      {getStatusIcon(order.status)}
                      {order.status}
                    </span>
                  </div>
                </div>

                {/* Order Items */}
                <div className="px-6 py-4" data-id="element-11354" data-mp="29iyt5zos7v7o">
                  {order.items.map((item, index) => {
              const isReviewed = reviewedItems.includes(item.id);
              return <div key={index} className={`flex flex-col sm:flex-row sm:items-center py-4 border-b last:border-0 ${dk.border} gap-4`} data-id="element-11355" data-mp="2ikojt3auaq4e" data-mpi={index}>
                        <div className={`h-20 w-14 flex-shrink-0 overflow-hidden rounded-md border ${dk.border}`} data-id="element-11356" data-mp="1qu1zny17l6qi">
                          <img src={item.image} alt={item.title} className="h-full w-full object-cover object-center" data-id="element-11357" data-mp="1zl292idv9pxf" />
                        </div>
                        <div className="flex-grow" data-id="element-11358" data-mp="1736peuf5f2uj">
                          <h3 className={`font-medium ${dk.textHeading}`} data-id="element-11359" data-mp="133xxzw7kzh25">
                            {item.title}
                          </h3>
                          <p className={`text-sm ${dk.textMuted}`} data-id="element-11360" data-mp="sy6csyl0ck54">
                            {item.format}
                          </p>
                          <p className={`font-medium mt-1 ${dk.textHeading}`} data-id="element-11361" data-mp="j1jvukvsyd8d">
                            ${item.price.toFixed(2)}
                          </p>
                        </div>

                        {/* Review Button for Delivered Items */}
                        {order.status === 'Delivered' && <div className="flex-shrink-0 mt-2 sm:mt-0" data-id="element-11362" data-mp="1gpbuljaidmi2">
                            {isReviewed ? <button onClick={() => openReviewModal(item)} className={`flex items-center px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${darkMode ? 'bg-green-900/20 text-green-400 hover:bg-green-900/40' : 'bg-green-50 text-green-700 hover:bg-green-100'}`} data-id="element-11363" data-mp="11qzut3ru9xmo">
                                <CheckCircleIcon className="h-4 w-4 mr-1.5" data-id="element-11364" data-mp="1xynwlbm84x4x" />
                                Reviewed
                              </button> : <button onClick={() => openReviewModal(item)} className={`flex items-center px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${darkMode ? 'bg-indigo-900/40 text-indigo-300 hover:bg-indigo-900/60' : 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100'}`} data-id="element-11365" data-mp="1o7t6fw5uxde7">
                                <StarIcon className="h-4 w-4 mr-1.5" data-id="element-11366" data-mp="159730amvba74" />
                                Write Review
                              </button>}
                          </div>}
                      </div>;
            })}
                </div>

                {/* Order Footer */}
                <div className={`px-6 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between border-t ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'}`} data-id="element-11367" data-mp="2umkgapcyk8kd">
                  <div className="flex space-x-2 mb-3 sm:mb-0" data-id="element-11368" data-mp="3by96hcl0ypsq">
                    <button className={`px-3 py-1 rounded-md text-sm font-medium flex items-center ${darkMode ? 'bg-indigo-900/40 text-indigo-300 hover:bg-indigo-900/60' : 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200'}`} data-id="element-11369" data-mp="3c02cqqrq7807">
                      <EyeIcon className="h-4 w-4 mr-1" data-id="element-11370" data-mp="2o96dx9ourcjg" />
                      View Details
                    </button>
                    <button className={`px-3 py-1 rounded-md text-sm font-medium flex items-center ${darkMode ? 'bg-blue-900/40 text-blue-300 hover:bg-blue-900/60' : 'bg-blue-100 text-blue-700 hover:bg-blue-200'}`} data-id="element-11371" data-mp="2t66mpsd36te9">
                      <RefreshCwIcon className="h-4 w-4 mr-1" data-id="element-11372" data-mp="dfk9sqx2gxpb" />
                      Reorder
                    </button>
                    <button className={`px-3 py-1 rounded-md text-sm font-medium flex items-center ${darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-11373" data-mp="ykxk59hqpirz">
                      <MessageSquareIcon className="h-4 w-4 mr-1" data-id="element-11374" data-mp="13jqyt58rjftq" />
                      Support
                    </button>
                  </div>
                  <div className="flex items-center" data-id="element-11375" data-mp="1h9ax9yvb9qx8">
                    <span className={`text-sm mr-2 ${dk.textMuted}`} data-id="element-11376" data-mp="1d9tpuzwbqjpf">
                      Total:
                    </span>
                    <span className={`text-lg font-bold ${dk.textHeading}`} data-id="element-11377" data-mp="1r7qp6425x47s">
                      ${order.total.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>) : <div className={`rounded-lg shadow-md p-8 text-center border ${dk.card}`} data-id="element-11378" data-mp="3t29h82t783wx">
              <p className={dk.textMuted} data-id="element-11379" data-mp="23jkpmr26x1wo">
                No orders found matching your filters.
              </p>
              <button onClick={() => {
            setSearchQuery('');
            setStatusFilter('All');
          }} className={`mt-4 px-4 py-2 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 ${rt.primaryButton}`} data-id="element-11380" data-mp="28ixetigjal2f">
                Clear Filters
              </button>
            </div>}
        </div>

        {/* Pagination */}
        {filteredOrders.length > 0 && <div className="flex justify-between items-center mt-6" data-id="element-11381" data-mp="1aun7qp1w574g">
            <button className={`px-3 py-1 border rounded-md text-sm font-medium flex items-center hover:opacity-80 ${dk.card} ${dk.border} ${dk.textSecondary}`} data-id="element-11382" data-mp="1aw6saruky4c1">
              <ChevronLeftIcon className="h-4 w-4 mr-1" data-id="element-11383" data-mp="3596amsqchcn0" />
              Previous
            </button>
            <div className={`text-sm ${dk.textMuted}`} data-id="element-11384" data-mp="3a77tpdnrtbyh">Page 1 of 1</div>
            <button className={`px-3 py-1 border rounded-md text-sm font-medium flex items-center hover:opacity-80 ${dk.card} ${dk.border} ${dk.textSecondary}`} data-id="element-11385" data-mp="hm9pyf4o4adi">
              Next
              <ChevronRightIcon className="h-4 w-4 ml-1" data-id="element-11386" data-mp="8mwfbj4t5fr7" />
            </button>
          </div>}
      </div>

      <WriteReviewModal isOpen={isReviewModalOpen} onClose={() => setIsReviewModalOpen(false)} subject={reviewSubject} onSave={handleSaveReview} data-id="element-11387" data-mp="dlg3lq6vj3mq" />
    </div>;
};
export default MyOrders;